import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { BookOpen, ChevronDown, Layers3, Play, Shuffle } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { PoliticsActiveSessionDialog } from "@/components/politics-session-dialog";
import { Button, EmptyState, Field, LoadingLine, PageHeader, Tag, cx } from "@/components/primitives";
import { ApiError, apiFetch } from "@/lib/api";
import {
  QUESTION_ORDER_LABELS,
  QUESTION_TYPE_LABELS,
  politicsSessionRequestBody,
  type PoliticsBank,
  type PoliticsQuestionOrder,
  type PoliticsQuestionTypeFilter,
  type PoliticsSection,
  type PoliticsSession,
  type PoliticsSessionSummary,
} from "@/lib/politics";

interface PendingSession {
  clientRequestId: string;
  bankId: string;
  source: "bank" | "section";
  sectionId?: string;
  questionType: PoliticsQuestionTypeFilter;
  order: PoliticsQuestionOrder;
  count: number;
}

export function PoliticsQuestionBankPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bankId, setBankId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [questionType, setQuestionType] = useState<PoliticsQuestionTypeFilter>("all");
  const [order, setOrder] = useState<PoliticsQuestionOrder>("sequential");
  const [count, setCount] = useState(20);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState<PendingSession | null>(null);
  const [activeSession, setActiveSession] = useState<PoliticsSessionSummary | null>(null);

  const banks = useQuery({
    queryKey: ["politics", "banks"],
    queryFn: () => apiFetch<{ banks: PoliticsBank[] }>("/politics/banks"),
  });
  useEffect(() => {
    const bankRows = banks.data?.banks ?? [];
    const firstBank = bankRows[0];
    if (firstBank && !bankRows.some((bank) => bank.id === bankId)) {
      setBankId(firstBank.id);
      setSectionId("");
    }
  }, [bankId, banks.data?.banks]);
  const sections = useQuery({
    queryKey: ["politics", "banks", bankId, "sections"],
    queryFn: () => apiFetch<{ bank: PoliticsBank; sections: PoliticsSection[] }>(`/politics/banks/${bankId}/sections`),
    enabled: Boolean(bankId),
  });
  const flatSections = useMemo(() => {
    const values: Array<PoliticsSection & { depth: number }> = [];
    const visit = (items: PoliticsSection[], depth: number) => {
      for (const item of items) {
        values.push({ ...item, depth });
        visit(item.children ?? [], depth + 1);
      }
    };
    visit(sections.data?.sections ?? [], 0);
    return values;
  }, [sections.data?.sections]);
  const selectedSection = flatSections.find((section) => section.id === sectionId);

  const createSession = async (request: PendingSession, replaceActive = false) => {
    setBusy(true);
    setNotice("");
    try {
      const response = await apiFetch<{ session: PoliticsSession }>("/politics/sessions", {
        method: "POST",
        body: JSON.stringify(politicsSessionRequestBody(request, replaceActive)),
      });
      setActiveSession(null);
      setPending(null);
      await queryClient.invalidateQueries({ queryKey: ["politics"] });
      await navigate({
        to: "/app/$subject/practice/$sessionId",
        params: { subject: "politics", sessionId: response.session.id },
      });
    } catch (error) {
      if (error instanceof ApiError && error.message === "active_session_exists") {
        const body = error.body as { activeSession?: PoliticsSessionSummary };
        if (body.activeSession) {
          setPending(request);
          setActiveSession(body.activeSession);
          return;
        }
      }
      setNotice(error instanceof Error && error.message === "no_questions"
        ? "当前范围没有符合条件的题目，请调整筛选。"
        : "练习创建失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!bankId) return;
    const request: PendingSession = {
      clientRequestId: crypto.randomUUID(),
      bankId,
      source: sectionId ? "section" : "bank",
      ...(sectionId ? { sectionId } : {}),
      questionType,
      order,
      count: Math.max(1, Math.min(100, count)),
    };
    void createSession(request);
  };

  const activeBank = banks.data?.banks.find((bank) => bank.id === bankId);
  return (
    <div className="page politics-bank-page">
      <PageHeader title="政治题库" description="选择课程、章节与题型，生成一组独立的选择题练习。" />
      {banks.isLoading ? <LoadingLine /> : null}
      {banks.isError ? <EmptyState title="题库暂不可用" description="请稍后重试，或联系管理员检查题库状态。" /> : null}
      {banks.data && banks.data.banks.length === 0 ? (
        <EmptyState title="还没有可练习的题库" description="政治题库发布并开放后会显示在这里。" />
      ) : null}
      {activeBank ? (
        <div className="politics-bank-layout">
          <aside className="politics-section-browser">
            <header>
              <p className="section-label">练习范围</p>
              <h2>课程与章节</h2>
              <p>{activeBank.name} · 共 {activeBank.questionCount} 题</p>
            </header>
            {banks.data && banks.data.banks.length > 1 ? (
              <label className="politics-bank-select">
                <span>题库版本</span>
                <select className="input" value={bankId} onChange={(event) => { setBankId(event.target.value); setSectionId(""); }}>
                  {banks.data.banks.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}
                </select>
                <ChevronDown />
              </label>
            ) : null}
            <button
              className={cx("politics-section-option", !sectionId && "politics-section-option--active")}
              type="button"
              onClick={() => setSectionId("")}
            >
              <span><Layers3 /><strong>全部课程</strong></span>
              <small>{activeBank.questionCount} 题</small>
            </button>
            <div className="politics-section-tree">
              {sections.isLoading ? <LoadingLine /> : null}
              {flatSections.map((section) => (
                <button
                  className={cx(
                    "politics-section-option",
                    sectionId === section.id && "politics-section-option--active",
                    section.depth > 0 && "politics-section-option--child",
                  )}
                  type="button"
                  key={section.id}
                  onClick={() => setSectionId(section.id)}
                >
                  <span><BookOpen /><strong>{section.name}</strong></span>
                  <small>{section.questionCount} 题</small>
                </button>
              ))}
            </div>
          </aside>

          <form className="politics-practice-builder" onSubmit={submit}>
            <header>
              <div><p className="section-label">练习设置</p><h2>{selectedSection?.name ?? "全部课程"}</h2></div>
              <Tag tone="accent">{selectedSection?.questionCount ?? activeBank.questionCount} 题可选</Tag>
            </header>
            <Field label="题型" htmlFor="politics-question-type">
              <div id="politics-question-type" className="politics-choice-grid politics-choice-grid--three">
                {(Object.keys(QUESTION_TYPE_LABELS) as PoliticsQuestionTypeFilter[]).map((value) => (
                  <button className={questionType === value ? "active" : ""} type="button" key={value} onClick={() => setQuestionType(value)}>
                    {QUESTION_TYPE_LABELS[value]}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="顺序" htmlFor="politics-question-order">
              <div id="politics-question-order" className="politics-choice-grid">
                {(Object.keys(QUESTION_ORDER_LABELS) as PoliticsQuestionOrder[]).map((value) => (
                  <button className={order === value ? "active" : ""} type="button" key={value} onClick={() => setOrder(value)}>
                    {value === "random" ? <Shuffle /> : <BookOpen />}
                    {QUESTION_ORDER_LABELS[value]}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="本组题量" htmlFor="politics-question-count" hint="若当前范围不足，将使用全部可用题目。">
              <div className="politics-count-control">
                <input
                  id="politics-question-count"
                  type="range"
                  min={1}
                  max={100}
                  value={count}
                  onChange={(event) => setCount(Number(event.target.value))}
                />
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={100}
                  value={count}
                  onChange={(event) => setCount(Number(event.target.value))}
                />
                <span>题</span>
              </div>
            </Field>
            {notice ? <p className="politics-inline-notice" role="status">{notice}</p> : null}
            <Button type="submit" variant="primary" size="lg" disabled={busy || !bankId}>
              <Play /> {busy ? "正在创建…" : "开始练习"}
            </Button>
          </form>
        </div>
      ) : null}
      {activeSession && pending ? (
        <PoliticsActiveSessionDialog
          session={activeSession}
          busy={busy}
          onCancel={() => { setActiveSession(null); setPending(null); }}
          onResume={() => void navigate({ to: "/app/$subject/practice/$sessionId", params: { subject: "politics", sessionId: activeSession.id } })}
          onReplace={() => void createSession(pending, true)}
        />
      ) : null}
    </div>
  );
}
